import { Component, OnDestroy, OnInit } from '@angular/core';
import { BreadCrumb } from '@nationwide/internet-servicing-angular-components';
import { CommercialClaimInfo } from 'app/commercial/models/claim-info.model';
import { hasOwnPropertyDescriptor } from 'app/commercial/models/object-property.util';
import { environment } from 'environments/environment';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { commercialConstants } from '../../../../../common/commercial-constants';
import { LoggerService } from '../../../../../shared/services/logger.service';
import { ClaimsDataService } from '../../../services/claims-data.service';
import { CommercialLogMetadata, CommercialLoggingService } from '../../../services/commercial-logging.service';
import { GetClaimsService } from '../../../services/get-claims.service';

@Component({
    selector: 'is-payments',
    templateUrl: './payments.component.html',
    styleUrls: ['./payments.component.scss'],
    standalone: false
})

export class PaymentsComponent implements OnInit, OnDestroy {
    breadCrumbs: BreadCrumb[] = [];
    claim = '';
    claimInfo: CommercialClaimInfo | null = null;
    currentPage = 'Payment Details';
    displayNames: any[] = [];
    displayPayments: any[] = [];
    hasMore = false;
    hasPayments = false;
    homeIconLink = environment.NATIONWIDE.accountSummaryPage;
    isWorkersComp = false;
    issuedCodes = '';
    legacyClaim = false;
    legacyClaimCutoffDate: any;
    // eslint-disable-next-line no-magic-numbers
    limit = 30;
    lossCause = false;
    nwFilter: any;
    paymentSubscription = new Subscription();
    payments: any[] = [];
    // eslint-disable-next-line no-magic-numbers
    perPage = 5;
    displayOffset = this.perPage; // eslint-disable-line @typescript-eslint/member-ordering
    policyType = '';
    serviceFlag = false;
    serviceOffset = 0;
    statusCodes: any;
    totalCount = null;
    totalPayments = '';

    constructor(
        private getClaimsService: GetClaimsService,
        public claimsDataService: ClaimsDataService,
        private LOGGER: LoggerService,
        private commercialLoggingService: CommercialLoggingService
    ) { }
    getFinancialInfo(): void {
        this.getPaymentStatusMetadata();
        this.getClaimsService.getFinancialSummary(this.claim).pipe(take(1)).subscribe((response) => {
            this.totalPayments = response.claimTotals.totalPaymentAmount;
            this.commercialLoggingService.logServiceSuccess(
                this.LOGGER,
                this.currentPage,
                'GetClaimsService',
                'getFinancialSummary',
                this.getLogMetadata({ claimNumber: this.claim })
            );
        }, (error) => {
            this.commercialLoggingService.logServiceFailure(
                this.LOGGER,
                this.currentPage,
                'GetClaimsService',
                'getFinancialSummary',
                this.getLogMetadata({ claimNumber: this.claim }),
                error
            );
        });
    }
    getPaymentStatusMetadata(): void {
        this.getClaimsService.getMetadata().pipe(take(1)).subscribe((response) => {
            this.statusCodes = response.metadata.value.codes;
            this.obtainFilters(response.metadata.value.filters);
            this.obtainDisplayNames(this.statusCodes);
            this.issuedCodes = this.getIssuedCodes(this.statusCodes);
            this.getPayments(this.serviceOffset);
            this.commercialLoggingService.logServiceSuccess(
                this.LOGGER,
                this.currentPage,
                'GetClaimsService',
                'getMetadata',
                this.getLogMetadata({ claimNumber: this.claim })
            );
        }, (error) => {
            this.commercialLoggingService.logServiceFailure(
                this.LOGGER,
                this.currentPage,
                'GetClaimsService',
                'getMetadata',
                this.getLogMetadata({ claimNumber: this.claim }),
                error
            );
        });
    }
    getPayments(offset: number): void {
        this.paymentSubscription =
            this.getClaimsService.getPayments(this.claim, this.issuedCodes, offset, this.limit).subscribe((response) => {
                this.totalCount = response.headers.get('X-Total-Count');
                const totalCountNumber = Number(this.totalCount || 0);
                if (totalCountNumber > 0) {
                    this.payments = this.payments.concat(response.body.getPayments || []);
                    this.hasPayments = true;
                }
                if (this.payments.length > 0) {
                    this.serviceOffset = this.serviceOffset + this.limit;
                    if (totalCountNumber > this.payments.length) {
                        this.getPayments(this.serviceOffset);
                    } else {
                        this.hasPayments = true;
                        this.serviceFlag = true;
                        this.updatePaymentMethods();
                        this.loadPaymentsForDisplay();
                        this.insertDisplayNameForPayments();
                        if (this.legacyClaim) {
                            this.prepareLegacyClaimInfo();
                        }
                    }
                } else {
                    this.hasPayments = false;
                    this.serviceFlag = true;
                    this.prepareLegacyClaimInfo();
                }
                this.commercialLoggingService.logServiceSuccess(
                    this.LOGGER,
                    this.currentPage,
                    'GetClaimsService',
                    'getPayments',
                    this.getLogMetadata({ claimNumber: this.claim, claimsDisplayCount: this.totalCount || '0' })
                );
            }, (error) => {
                this.commercialLoggingService.logServiceFailure(
                    this.LOGGER,
                    this.currentPage,
                    'GetClaimsService',
                    'getPayments',
                    this.getLogMetadata({ claimNumber: this.claim }),
                    error
                );
            });
    }

    insertDisplayNameForPayments(): void {
        for (const element of this.displayPayments) {
            element['displayName'] = this.displayNames[element.status];
        }
    }
    loadPaymentsForDisplay(): void {
        if (this.payments.length <= this.perPage) {
            this.displayPayments = this.payments;
            this.hasMore = false;
        } else {
            const currentOffset = this.displayOffset;
            this.displayPayments = this.payments.slice(0, currentOffset);
            this.displayOffset = this.displayOffset + this.perPage;
            if (this.payments.length <= this.displayPayments.length) {
                this.hasMore = false;
            } else {
                this.hasMore = true;
            }
        }
    }
    ngOnDestroy(): void {
        this.paymentSubscription.unsubscribe();
    }

    ngOnInit(): void {
        this.claimInfo = this.claimsDataService.getClaimInformation() as CommercialClaimInfo;
        this.legacyClaim = !!this.claimInfo?.legacyClaim;
        this.policyType = this.claimsDataService.getClaimsPolicyType();
        this.isWorkersComp = this.policyType === 'Workers Compensation';
        this.lossCause = hasOwnPropertyDescriptor(this.claimInfo, 'lossCause');
        this.claim = this.claimsDataService.getClaimNumber();
        this.getFinancialInfo();
        this.breadCrumbs = [
            {
                name: 'Package (PAK) Claims',
                link: `${commercialConstants.URLS.pakClaimsList}`
            }, {
                name: 'Claim Overview',
                link: `${commercialConstants.URLS.claimOverview}`
            }
        ];
        this.commercialLoggingService.logPageLoad(this.LOGGER, this.currentPage, this.getLogMetadata({ claimNumber: this.claim }));
        this.commercialLoggingService.updatePreviousUrlContext();
    }

    obtainDisplayNames(statusCodes: any): void {
        for (const statusCode of statusCodes) {
            if (this.nwFilter.includes(statusCode.code)) {
                this.displayNames[statusCode.code] = statusCode.displayName;
            }
        }
    }

    obtainFilters(filters: any): void {
        for (const element of filters) {
            if (element.name === 'nw_internet') {
                this.nwFilter = element.includedCodes;
            }
        }
    }
    prepareLegacyClaimInfo(): void {
        if (this.payments.length > 0) {
            const payment = this.payments[this.payments.length - 1];
            if (Object.getOwnPropertyDescriptor(payment, 'issueDate')) {
                this.legacyClaimCutoffDate = new Date();
            } else {
                this.legacyClaimCutoffDate = new Date(payment.issueDate);
            }
        }
    }
    showMore(): void {
        this.commercialLoggingService.logButtonAction(this.LOGGER, this.currentPage, 'show-more-payments', 'success', this.getLogMetadata({ claimNumber: this.claim }));
        this.loadPaymentsForDisplay();
    }

    updatePaymentMethods(): void {
        for (const payment of this.payments) {
            const paymentMethod = payment.paymentMethod;
            if (paymentMethod === 'manual' || paymentMethod === 'check') {
                payment.paymentMethod = 'Check';
            } else if (paymentMethod === 'eft') {
                payment.paymentMethod = 'Electronic Funds Transfer (EFT)';
            } else if (paymentMethod === 'dp') {
                payment.paymentMethod = 'Digital Payment';
            }
        }
    }

    private getIssuedCodes(statusCodes: any[]): string {
        const issuedCodeList: string[] = [];
        for (const status of statusCodes) {
            if (this.nwFilter.includes(status.code)) {
                issuedCodeList.push(status.code);
            }
        }
        return issuedCodeList.join(',');
    }

    private getLogMetadata(additionalFields: Partial<CommercialLogMetadata> = {}): Partial<CommercialLogMetadata> {
        return {
            policyNumber: this.claimsDataService.getClaimsPolicyNumber(),
            policyType: this.claimsDataService.getClaimsPolicyType(),
            lineOfBusiness: this.claimsDataService.getFnolFlow(),
            claimsDisplayCount: this.totalCount || this.claimsDataService.getClaimsDisplayCount() || 'UNKNOWN',
            alphaState: this.claimsDataService.getPrimary(this.claimsDataService.getClaimsPackage()?.address)?.state || 'UNKNOWN',
            previousUrl: window.sessionStorage.getItem('previousUrl') || '',
            claimNumber: this.claimInfo?.claimNumber || this.claim || '',
            claimStatus: this.claimInfo?.claimState || '',
            incidentDate: this.claimInfo?.lossDate || '',
            lossCause: this.claimInfo?.lossCause || '',
            ...additionalFields
        };
    }
}
